import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Music } from 'lucide-react';
import { toast } from 'sonner';

export default function BecomeArtist() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    stage_name: '',
    bio: '',
    genres: '',
    experience: '',
    social_links: '',
    portfolio_url: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.stage_name || !formData.bio) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      await api.applyForArtist(formData);
      toast.success('Application submitted successfully! We will review it shortly.');
      navigate('/profile');
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
            Become an Artist
          </h1>
          <p className="text-lg text-gray-400 mt-2">
            Get featured on Vibezlink and connect with fans
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="glass border-white/10 border-primary/50">
        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            <Music className="h-8 w-8 text-primary flex-shrink-0" />
            <div>
              <h3 className="font-heading font-bold mb-2">Why become an artist?</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>• Get featured in artist discovery</li>
                <li>• Build your fanbase on Vibezlink</li>
                <li>• Connect with event organizers</li>
                <li>• Showcase your music and portfolio</li>
                <li>• Engage with attendees at events</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Application Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Artist Application</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stage_name">Stage Name *</Label>
              <Input
                id="stage_name"
                value={formData.stage_name}
                onChange={(e) => setFormData({ ...formData, stage_name: e.target.value })}
                placeholder="Your artist/performer name"
                className="glass border-white/10"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Artist Bio *</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about yourself as an artist..."
                className="glass border-white/10 min-h-[120px]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="genres">Genres/Styles</Label>
              <Input
                id="genres"
                value={formData.genres}
                onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
                placeholder="e.g., EDM, Hip Hop, Rock (comma-separated)"
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="experience">Performance Experience</Label>
              <Textarea
                id="experience"
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                placeholder="Share your performance history, notable shows, etc."
                className="glass border-white/10 min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="portfolio_url">Portfolio/Music Links</Label>
              <Input
                id="portfolio_url"
                value={formData.portfolio_url}
                onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                placeholder="SoundCloud, Spotify, YouTube, etc."
                className="glass border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="social_links">Social Media (Optional)</Label>
              <Textarea
                id="social_links"
                value={formData.social_links}
                onChange={(e) => setFormData({ ...formData, social_links: e.target.value })}
                placeholder="Instagram, Twitter, TikTok, etc. (one per line)"
                className="glass border-white/10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex space-x-4">
          <Button
            type="button"
            onClick={() => navigate('/profile')}
            variant="outline"
            className="flex-1 glass-hover"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 shadow-glow"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </div>
      </form>
    </div>
  );
}
